import Vue from 'vue';
import { assign } from 'lodash';
import * as api from 'api/api-system-administration';
import * as types from '../mutation-types';

const initState = {
  ready: false,
  fetching: false,
  fetched: true,
  saving: false,
  saveError: null,
  saved: null,
  error: null,
  notFound: null,
  meta: {},
  data: {},
};

const neutralState = {
  fetching: false,
  fetched: true,
  saving: false,
  saveError: null,
  saved: null,
  error: null,
  notFound: null,
};

const state = {};

const getters = {
  SysAdminInstanceSubscriptionInvoiceDetails: state => invoiceID => state[invoiceID],
};

const actions = {
  GetSysAdminInstanceSubscriptionInvoiceDetails({ commit }, params) {
    const instanceID = params.instanceID;
    const subscriptionID = params.subscriptionID;
    const invoiceID = params.invoiceID;

    commit(types.FETCH_SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_START, { instanceID, subscriptionID, invoiceID });
    api.getAdminInstanceSubscriptionInvoiceDetails(
      instanceID,
      subscriptionID,
      invoiceID,
      (response) => {
        commit(types.RECEIVE_SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS, { invoiceID, response });
      },
      (description, status, error) => {
        if (status === 404) {
          commit(types.FETCH_SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_NOT_FOUND, { invoiceID, error });
        } else {
          commit(types.FETCH_SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_ERROR, { invoiceID, error });
        }
      },
    );
  },
  SysAdminInstanceSubscriptionInvoiceDetailsSetConfirmPayment({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      const instanceID = params.instanceID;
      const subscriptionID = params.subscriptionID;
      const invoiceID = params.invoiceID;
      const payload = params.payload;

      commit(types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_CONFIRM_PAYMENT_START, { invoiceID });
      api.setAdminInstanceSubscriptionInvoiceConfirmPayment(
        instanceID,
        subscriptionID,
        invoiceID,
        payload,
        (response) => {
          commit(types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_CONFIRM_PAYMENT_START_SUCCESSFUL, { invoiceID, response });
          dispatch('GetSysAdminInstanceSubscriptionInvoicesList', { instanceID, subscriptionID });
          resolve(true);
        },
        (description, status, saveError) => {
          commit(types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_CONFIRM_PAYMENT_START_ERROR, { invoiceID, saveError });
          reject(saveError);
        },
      );
    });
  },
  SysAdminInstanceSubscriptionInvoiceDetailsSetDenyPayment({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      const instanceID = params.instanceID;
      const subscriptionID = params.subscriptionID;
      const invoiceID = params.invoiceID;
      const payload = {};

      commit(types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_DENY_PAYMENT_START, { invoiceID });
      api.setAdminInstanceSubscriptionInvoiceDenyPayment(
        instanceID,
        subscriptionID,
        invoiceID,
        payload,
        (response) => {
          commit(types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_DENY_PAYMENT_START_SUCCESSFUL, { invoiceID, response });
          resolve(true);
        },
        (description, status, saveError) => {
          commit(types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_DENY_PAYMENT_START_ERROR, { invoiceID, saveError });
          reject(saveError);
        },
      );
    });
  },
  SysAdminInstanceSubscriptionInvoiceDetailsSetCancelPayment({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      const instanceID = params.instanceID;
      const subscriptionID = params.subscriptionID;
      const invoiceID = params.invoiceID;
      const payload = {};

      commit(types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_CANCEL_PAYMENT_START, { invoiceID });
      api.setAdminInstanceSubscriptionInvoiceCancelPayment(
        instanceID,
        subscriptionID,
        invoiceID,
        payload,
        (response) => {
          commit(types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_CANCEL_PAYMENT_START_SUCCESSFUL, { invoiceID, response });
          resolve(true);
        },
        (description, status, saveError) => {
          commit(types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_CANCEL_PAYMENT_START_ERROR, { invoiceID, saveError });
          reject(saveError);
        },
      );
    });
  },
  InitSysAdminInstanceSubscriptionInvoiceDetails({ commit }, params) {
    const invoiceID = params.invoiceID;
    commit(types.CLEAR_SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_STORE, { invoiceID });
  },
  ClearSysAdminInstanceSubscriptionInvoiceDetails({ commit }, { invoiceID }) {
    commit(types.CLEAR_SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_STORE, { invoiceID });
  },
};

const mutations = {
  [types.FETCH_SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_START](state, { invoiceID }) {
    if (state[invoiceID]) Vue.set(state, invoiceID, assign(state[invoiceID], neutralState, { fetching: true }));
    else Vue.set(state, invoiceID, assign({}, initState, { fetching: true }));
  },
  [types.FETCH_SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_ERROR](state, { invoiceID, error }) {
    Vue.set(state, invoiceID, assign(state[invoiceID], neutralState, { error }));
  },
  [types.FETCH_SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_NOT_FOUND](state, { invoiceID }) {
    Vue.set(state, invoiceID, assign({}, neutralState, { notFound: true }));
  },
  [types.RECEIVE_SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS](state, { invoiceID, response }) {
    Vue.set(state, invoiceID, assign(state[invoiceID], neutralState, { ready: true, data: response.data, meta: response.meta }));
  },

  [types.CLEAR_SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_STORE](state, { invoiceID }) {
    Vue.set(state, invoiceID, assign({}, initState));
  },

  [types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_CONFIRM_PAYMENT_START](state, { invoiceID }) {
    Vue.set(state, invoiceID, assign(state[invoiceID], neutralState, { saving: true }));
  },
  [types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_CONFIRM_PAYMENT_START_ERROR](state, { invoiceID, saveError }) {
    Vue.set(state, invoiceID, assign(state[invoiceID], neutralState, { saveError }));
  },
  [types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_CONFIRM_PAYMENT_START_SUCCESSFUL](state, { invoiceID, response }) {
    Vue.set(state, invoiceID, assign(state[invoiceID], neutralState, { saved: true, data: response.data, meta: response.meta }));
  },

  [types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_DENY_PAYMENT_START](state, { invoiceID }) {
    Vue.set(state, invoiceID, assign(state[invoiceID], neutralState, { saving: true }));
  },
  [types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_DENY_PAYMENT_START_ERROR](state, { invoiceID, saveError }) {
    Vue.set(state, invoiceID, assign(state[invoiceID], neutralState, { saveError }));
  },
  [types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_DENY_PAYMENT_START_SUCCESSFUL](state, { invoiceID, response }) {
    Vue.set(state, invoiceID, assign(state[invoiceID], neutralState, { saved: true, data: response.data, meta: response.meta }));
  },

  [types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_CANCEL_PAYMENT_START](state, { invoiceID }) {
    Vue.set(state, invoiceID, assign(state[invoiceID], neutralState, { saving: true }));
  },
  [types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_CANCEL_PAYMENT_START_ERROR](state, { invoiceID, saveError }) {
    Vue.set(state, invoiceID, assign(state[invoiceID], neutralState, { saveError }));
  },
  [types.SYS_ADMIN_INSTANCE_SUBSCRIPTION_INVOICE_DETAILS_SET_CANCEL_PAYMENT_START_SUCCESSFUL](state, { invoiceID, response }) {
    Vue.set(state, invoiceID, assign(state[invoiceID], neutralState, { saved: true, data: response.data, meta: response.meta }));
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/system-administration/entities/sys-admin-instance-subscription-invoice-details.js